/**
 * Where a first-party app is, and who is asking — the five answers every connector needs before
 * it can make a single request.
 *
 * This file existed **44 times** before 2026-09-30, once per connector, at 6 distinct md5s of
 * which 39 were two variants differing by a comment. The only behavioural difference across all
 * of them was how `getAuthToken` narrowed its regex group, and the form kept below is the one
 * that does it without a non-null assertion.
 *
 * Nothing here is app-specific, which is the argument for one copy: every value is derived from
 * `window.location` or `document.cookie` at call time. A connector passes its own service name
 * and gets its own host back.
 *
 * ⚠️ These are **defaults**, read at construction time by a client that accepts overrides. An
 * `alex` build does not patch this file; it passes `host: 'time.api.quantumalex.com'` — which is
 * exactly why `apexDomain` reads the browser rather than a build-time constant.
 */

/** The apex a dev server, an empty host or a bare label falls back to. */
export const FALLBACK_APEX_DOMAIN = 'martincastroalvarez.com';
/** The session token's cookie. HttpOnly since 2026-10-02 — see {@link getAuthToken}. */
export const JWT_COOKIE = 'jwt';

/**
 * The apex domain — the last two labels of the browser host.
 *
 * Falls back for localhost, `127.*`, `0.*` and SSR, where there is no tenant to read: a dev server
 * on `localhost:5173` is working against the martin tenant's API, and guessing `localhost` as an
 * apex would build `time.api.localhost`.
 */
export function apexDomain(): string {
  if (apexOverride) return apexOverride;
  return apexFromHost(typeof window !== 'undefined' ? window.location?.hostname : undefined);
}

/**
 * The apex of `hostname` — its last two labels, `time.api.quantumalex.com` → `quantumalex.com` —
 * or the fallback for a dev host (localhost, `127.*`, `0.*`), an empty one or a bare label.
 */
export function apexFromHost(hostname: string | undefined): string {
  const host = (hostname ?? '').trim().toLowerCase();
  if (
    !host ||
    host === 'localhost' ||
    host.endsWith('.localhost') ||
    host.startsWith('127.') ||
    host.startsWith('0.')
  ) {
    return FALLBACK_APEX_DOMAIN;
  }
  const parts = host.split('.').filter(Boolean);
  return parts.length >= 2 ? parts.slice(-2).join('.') : FALLBACK_APEX_DOMAIN;
}

let apexOverride: string | undefined;

/**
 * Pin the apex for this page — a build that knows its tenant (`VITE_APEX_DOMAIN`) or a local dev
 * server pointed at another tenant. Empty clears the pin and the browser host decides again. The
 * one copy of what ~35 `main.tsx` / `apex.ts` files each derived by hand (ralint R003).
 */
export function setApexDomain(domain: string | undefined): void {
  const pinned = typeof domain === 'string' ? domain.trim().toLowerCase() : '';
  apexOverride = pinned || undefined;
}

/** Default API host for a first-party service: `<service>.api.<apex>`. */
export function apiHost(service: string): string {
  return `${service}.api.${apexDomain()}`;
}

/** Default first-party web host: `<service>.<apex>` (a sibling app's URL). */
export function webHost(service: string): string {
  return `${service}.${apexDomain()}`;
}

/** A sibling app's URL, `https://<service>.<apex><path>` — `webUrl('orders', '/books')`. */
export function webUrl(service: string, path = ''): string {
  return `https://${webHost(service)}${path}`;
}

/**
 * A cookie by exact name, URL-decoded, or `null`. `null` outside the browser.
 *
 * ⚠️ The value is bound to a local before it is decoded. `match[1]` is `string | undefined` under
 * `noUncheckedIndexedAccess` even though a successful match always has the group — the compiler
 * cannot know the pattern has one — so checking the VALUE is what narrows it. Five connectors
 * wrote `match[1]!` instead; the assertion is unnecessary and this form is also correct for a
 * cookie explicitly set to the empty string.
 */
export function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  const value = match?.[1];
  if (value === undefined) return null;
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/**
 * A session token page script can READ, for the `X-Auth` header — or `null`, which is now the
 * normal answer.
 *
 * Since 2026-10-02 login sets the `jwt` cookie **HttpOnly**: the browser attaches it to every
 * fleet API call by itself (clients send `withCredentials` / `credentials: 'include'`), and no
 * script can read it. What this still returns is a LEGACY readable `jwt` cookie written by the old
 * sign-in page, until that session ends. It is for attaching `X-Auth` and nothing else — to ask
 * "is somebody signed in?" use `hasSession()` (`session.ts`), which also sees the HttpOnly session
 * through its readable `session` hint cookie.
 */
export function getAuthToken(): string | null {
  return readCookie(JWT_COOKIE);
}

/**
 * Send the person to login, returning them to the current page afterwards — or, when `error` is an
 * app refusing somebody who has not BOUGHT it (horus `NotEntitledError`, 2026-10-20), to the store
 * instead: `https://store.<apex>/apps/<this app>?next=<this page>`. The store finds the first
 * product for sale that contains the app and opens its page; with none it sends the person on to
 * login with the same `next`. Login would only hand a signed-in person the same session back.
 *
 * A connector's response interceptor passes the error it caught (`onUnauthorized(error)`); called
 * with nothing it is the plain login bounce, as before.
 */
export function redirectToLogin(error?: unknown): void {
  if (typeof window === 'undefined') return;
  const next = encodeURIComponent(window.location.href);
  const app = appName();
  window.location.href =
    isNotEntitled(error) && app
      ? `https://store.${apexDomain()}/apps/${encodeURIComponent(app)}?next=${next}`
      : `https://login.${apexDomain()}/?next=${next}`;
}

/** This page's app: the first label of its host (`time.quantumalex.com` → `time`), or `""`. */
export function appName(): string {
  if (typeof window === 'undefined') return '';
  const host = (window.location?.hostname ?? '').toLowerCase();
  const apex = apexDomain();
  if (!host.endsWith(`.${apex}`)) return '';
  return (
    host
      .slice(0, -(apex.length + 1))
      .split('.')
      .pop() ?? ''
  );
}

/**
 * True for horus' `NotEntitledError` — a signed-in person refused because no purchase of theirs
 * contains this app (`{"error": {"type": "NotEntitledError"}}` on a 403). Any other 403 is not.
 */
export function isNotEntitled(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: unknown } })?.response;
  if (response?.status !== 403) return false;
  const data = response.data as { error?: { type?: unknown } } | null | undefined;
  return data?.error?.type === 'NotEntitledError';
}
